import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, user, status } = useAuth();
  const next = params.get("next") || null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status === "authenticated" && user) {
      const dest =
        next || (user.onboarding_completed ? "/app" : "/onboarding");
      navigate(dest, { replace: true });
    }
  }, [status, user, navigate, next]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email: email.trim(), password });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Login failed.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      testId="login-page"
      eyebrow="Welcome back"
      heading="Sign in to your wardrobe."
      description={
        <>
          New here?{" "}
          <Link
            to="/signup"
            data-testid="login-link-signup"
            className="text-oxblood hover:underline underline-offset-4"
          >
            Start a free trial
          </Link>
          .
        </>
      }
    >
      <form
        data-testid="login-form"
        onSubmit={onSubmit}
        className="space-y-5"
        noValidate
      >
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            data-testid="login-email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            autoComplete="email"
            required
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/forgot-password"
              data-testid="login-forgot-password"
              className="text-xs text-muted-foreground hover:text-oxblood transition-colors"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            data-testid="login-password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            autoComplete="current-password"
            required
          />
        </div>

        {error && (
          <p
            role="alert"
            data-testid="login-error"
            className="text-sm text-oxblood border border-oxblood/30 bg-oxblood/5 px-3 py-2"
          >
            {error}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          data-testid="login-submit"
          disabled={!email || !password || submitting}
          className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthLayout>
  );
}
